const NEWS_ITEM_KEYS = ["title", "date", "url", "sentiment"] as const;
const PEER_KEYS = ["symbol", "name", "peRatio"] as const;

/** Fields the stock UI may consume from GET /stock (quote + analysis). */
const ANALYSIS_PAYLOAD_KEYS = [
  "symbol",
  "name",
  "quotedAt",
  "price",
  "change",
  "changePercent",
  "marketCap",
  "sector",
  "industry",
  "revenueGrowth",
  "profitMargin",
  "debtToEquity",
  "healthStatus",
  "peRatio",
  "industryPE",
  "pbRatio",
  "dividendYield",
  "paymentFrequency",
  "paysDividend",
  "beta",
  "riskLevel",
  "volatilityScore",
  "overview",
  "aiSummary",
  "news",
  "peers",
] as const;

function pickRecord(
  source: Record<string, unknown>,
  keys: readonly string[]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    if (key in source) {
      out[key] = source[key];
    }
  }
  return out;
}

function sanitizeNewsItem(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== "object") {
    return null;
  }
  return pickRecord(item as Record<string, unknown>, [...NEWS_ITEM_KEYS]);
}

function sanitizePeer(peer: unknown): Record<string, unknown> | null {
  if (!peer || typeof peer !== "object") {
    return null;
  }
  return pickRecord(peer as Record<string, unknown>, [...PEER_KEYS]);
}

/**
 * Keep only fields the stock UI uses; drops synthetic analyst labels and legacy score/verdict blobs.
 */
export function sanitizeStockAnalysisPayload(
  payload: unknown
): Record<string, unknown> {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  const safe = pickRecord(payload as Record<string, unknown>, [
    ...ANALYSIS_PAYLOAD_KEYS,
  ]);

  if (Array.isArray(safe.news)) {
    safe.news = safe.news
      .map(sanitizeNewsItem)
      .filter((item): item is Record<string, unknown> => item != null);
  }

  if (Array.isArray(safe.peers)) {
    safe.peers = safe.peers
      .map(sanitizePeer)
      .filter((peer): peer is Record<string, unknown> => peer != null);
  }

  return safe;
}
